import React, { useState, useEffect, useCallback } from 'react'
import * as api from '../../../lib/api'
import { useToast } from '../../../contexts/ToastContext'
import Modal from '../../../components/Modal'
import CustomSelect from '../../../components/CustomSelect/CustomSelect'
import { OperationsSkeleton } from '../../../components/Skeleton/Skeleton'
import DatePickerField from '../../../components/DatePickerField'
import { proxyStorageUrl } from '../../../lib/supabase'

// Helper for formatting numbers with spaces (e.g. 1000 -> 1 000)
const formatNumberWithSpaces = (val) => {
  if (val === null || val === undefined || val === '') return ''
  const clean = val.toString().replace(/[^\d.]/g, '')
  const parts = clean.split('.')
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
  return parts.join('.')
}

// Helper for formatting card numbers or phone numbers
const formatPaymentDetails = (val) => {
  if (!val) return ''

  const digits = val.replace(/\D/g, '')
  if (digits.length === 0) return val

  const isCard = digits.length >= 13 && /^\s*\d/.test(val) && !/[a-zA-Zа-яА-Я]/.test(val.slice(0, 20))

  if (isCard) {
    const cardDigits = digits.slice(0, 16)
    const formattedCard = cardDigits.replace(/(\d{4})/g, '$1 ').trim()
    
    let suffix = ''
    let digitCount = 0
    for (let i = 0; i < val.length; i++) {
      if (/\d/.test(val[i])) {
        digitCount++
        if (digitCount === 16) {
          suffix = val.slice(i + 1)
          break
        }
      }
    }
    return formattedCard + suffix
  }

  const isPhone = /^\+?[789\s()]/.test(val)

  if (isPhone) {
    let cleaned = digits
    let hasPrefix = false
    if (cleaned.startsWith('7') || cleaned.startsWith('8')) {
      cleaned = cleaned.slice(1)
      hasPrefix = true
    }

    const phonePart = cleaned.slice(0, 10)
    let suffix = ''
    let digitCount = 0
    const targetDigitIndex = hasPrefix ? 11 : 10
    
    for (let i = 0; i < val.length; i++) {
      if (/\d/.test(val[i])) {
        digitCount++
        if (digitCount === targetDigitIndex) {
          suffix = val.slice(i + 1)
          break
        }
      }
    }

    if (suffix === '' && val.length > 0) {
      const lastChar = val[val.length - 1]
      if (/\s/.test(lastChar) && digits.length === (hasPrefix ? 11 : 10)) {
        suffix = ' '
      }
    }

    if (phonePart.length === 0) return val

    let formatted = '+7 '
    if (phonePart.length > 0) {
      formatted += '(' + phonePart.slice(0, Math.min(phonePart.length, 3))
    }
    if (phonePart.length >= 3) {
      formatted += ') ' + phonePart.slice(3, Math.min(phonePart.length, 6))
    }
    if (phonePart.length >= 6) {
      formatted += '-' + phonePart.slice(6, Math.min(phonePart.length, 8))
    }
    if (phonePart.length >= 8) {
      formatted += '-' + phonePart.slice(8, Math.min(phonePart.length, 10))
    }

    return formatted + suffix
  }

  return val
}

const extractCleanNumberForCopy = (text) => {
  if (!text) return ''
  
  const digits = text.replace(/\D/g, '')
  
  if (digits.length >= 13 && digits.length <= 19) {
    return digits.slice(0, 16)
  }
  
  if (digits.length >= 10 && digits.length <= 11) {
    if (digits.length === 11 && (digits.startsWith('7') || digits.startsWith('8'))) {
      return '+7' + digits.slice(1)
    }
    if (digits.length === 10) {
      return '+7' + digits
    }
  }

  const firstWord = text.trim().split(/\s+/)[0]
  const cleanFirstWord = firstWord.replace(/[^\d+]/g, '')
  if (cleanFirstWord.length >= 10) {
    return cleanFirstWord
  }

  return cleanFirstWord || text
}

export default function SupplyRequests({
  profile,
  isAdmin,
  isOwner,
  userPermissions,
  activePvz,
  employees,
  loadRecords,
  selectedPvzIds,
  userPvzs,
  getPvzColor,
  getPvzShortName,
  getPvzFullName
}) {
  const toast = useToast()

  const renderPvzBadge = (pvzId) => {
    if (!pvzId) return null
    if (!selectedPvzIds || selectedPvzIds.length <= 1) return null
    const shortName = getPvzShortName ? getPvzShortName(pvzId) : ''
    const pvzColor = getPvzColor ? getPvzColor(pvzId) : 'var(--text-tertiary)'
    const pvzFullName = getPvzFullName ? getPvzFullName(pvzId) : 'Пункт выдачи'
    return (
      <span 
        style={{
          fontSize: '9px',
          fontWeight: 800,
          color: pvzColor,
          background: `color-mix(in srgb, ${pvzColor} 8%, var(--bg-secondary))`,
          border: `1px solid color-mix(in srgb, ${pvzColor} 30%, var(--surface-border))`,
          borderRadius: '4px',
          padding: '1px 4px',
          marginLeft: '8px',
          lineHeight: 1,
          display: 'inline-flex',
          alignItems: 'center'
        }}
        title={`Пункт: ${pvzFullName}`}
      >
        {shortName}
      </span>
    )
  }

  const [supplyRequests, setSupplyRequests] = useState([])
  const [loadingSupplies, setLoadingSupplies] = useState(false)
  const [showSupplyModal, setShowSupplyModal] = useState(false)
  const [supplyFormData, setSupplyFormData] = useState({
    title: '',
    description: '',
    amount: '',
    payment_details: '',
    payment_type: 'employee_details',
    employee_id: '',
    file: null,
    previewUrl: '',
    photo_url: '',
    pvzId: selectedPvzIds && selectedPvzIds.length > 0 ? selectedPvzIds[0] : activePvz?.id || ''
  })
  const [submittingSupply, setSubmittingSupply] = useState(false)
  const [recentPayments, setRecentPayments] = useState([])
  const [showPaySupplyModal, setShowPaySupplyModal] = useState(null)
  const [finalPayAmount, setFinalPayAmount] = useState('')
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(null)
  const [editingSupplyId, setEditingSupplyId] = useState(null)
  const [editingCommentId, setEditingCommentId] = useState(null)
  const [adminCommentText, setAdminCommentText] = useState('')
  const [supplyImagePreview, setSupplyImagePreview] = useState(null)

  const loadSupplyRequests = useCallback(async () => {
    if (!profile?.id) return
    const pvzFilter = selectedPvzIds?.length > 0 ? selectedPvzIds.join(',') : (activePvz?.id || '')
    if (!pvzFilter) return
    try {
      setLoadingSupplies(true)
      const data = await api.getSupplyRequests(pvzFilter, profile.org_id)
      setSupplyRequests(data)
    } catch (err) {
      console.error(err)
      toast.error('Ошибка загрузки заявок на хознужды')
    } finally {
      setLoadingSupplies(false)
    }
  }, [profile?.id, activePvz?.id, selectedPvzIds, profile?.org_id])

  useEffect(() => {
    loadSupplyRequests()

    const handleChanged = () => {
      loadSupplyRequests()
    }
    window.addEventListener('supply-requests-changed', handleChanged)
    return () => {
      window.removeEventListener('supply-requests-changed', handleChanged)
    }
  }, [loadSupplyRequests])

  const loadRecentPayments = async (empId) => {
    if (!empId) {
      setRecentPayments([])
      return
    }
    try {
      const history = await api.getLastSupplyPaymentDetailsHistory(empId)
      setRecentPayments(history || [])
    } catch (err) {
      console.warn('Error loading recent payments history:', err)
      setRecentPayments([])
    }
  }

  const handleSubmitSupplyRequest = async (e) => {
    e.preventDefault()
    if (!supplyFormData.title.trim()) {
      toast.error('Введите наименование расходников')
      return
    }
    
    setSubmittingSupply(true)
    try {
      let photoUrl = supplyFormData.photo_url || null
      if (supplyFormData.file) {
        photoUrl = await api.uploadEvidenceFile(supplyFormData.file)
      }

      const cleanAmount = supplyFormData.amount ? Number(supplyFormData.amount.toString().replace(/\s/g, '')) : null
      const cleanPayment = supplyFormData.payment_details ? supplyFormData.payment_details.trim() : null

      const targetPvzId = supplyFormData.pvzId || (selectedPvzIds && selectedPvzIds.length > 0 ? selectedPvzIds[0] : activePvz?.id || '')

      if (editingSupplyId) {
        await api.updateSupplyRequest(editingSupplyId, {
          title: supplyFormData.title.trim(),
          description: supplyFormData.description.trim() || null,
          amount: cleanAmount,
          payment_details: cleanPayment,
          payment_type: supplyFormData.payment_type || 'employee_details',
          employee_id: supplyFormData.employee_id || profile.id,
          photo_url: photoUrl,
          pvz_id: targetPvzId
        })
        toast.success('Заявка успешно отредактирована!')
      } else {
        await api.createSupplyRequest({
          org_id: profile.org_id,
          pvz_id: targetPvzId,
          employee_id: supplyFormData.employee_id || profile.id,
          title: supplyFormData.title.trim(),
          description: supplyFormData.description.trim() || null,
          amount: cleanAmount,
          payment_details: cleanPayment,
          payment_type: supplyFormData.payment_type || 'employee_details',
          photo_url: photoUrl,
          status: 'new'
        })

        toast.success('Заявка на хознужды успешно создана!')
      }

      setShowSupplyModal(false)
      setEditingSupplyId(null)
      setSupplyFormData({
        title: '',
        description: '',
        amount: '',
        payment_details: '',
        payment_type: 'employee_details',
        employee_id: '',
        file: null,
        previewUrl: '',
        photo_url: '',
        pvzId: selectedPvzIds && selectedPvzIds.length > 0 ? selectedPvzIds[0] : activePvz?.id || ''
      })
      loadSupplyRequests()
      window.dispatchEvent(new CustomEvent('supply-requests-changed'))
    } catch (err) {
      console.error(err)
      toast.error(editingSupplyId ? 'Ошибка при сохранении заявки' : 'Ошибка при создании заявки')
    } finally {
      setSubmittingSupply(false)
    }
  }

  const handleUpdateSupplyStatus = async (request, newStatus) => {
    if (newStatus === 'paid') {
      setFinalPayAmount(formatNumberWithSpaces(request.amount || ''))
      setShowPaySupplyModal(request)
      return
    }

    try {
      await api.updateSupplyRequest(request.id, { status: newStatus })
      toast.success(`Статус заявки изменен на: ${newStatus}`)
      loadSupplyRequests()
      window.dispatchEvent(new CustomEvent('supply-requests-changed'))
    } catch (err) {
      console.error(err)
      toast.error('Ошибка изменения статуса заявки')
    }
  }

  const handleUpdatePlannedPayDate = async (requestId, dateVal) => {
    try {
      await api.updateSupplyRequest(requestId, { planned_pay_date: dateVal || null })
      toast.success('Планируемая дата оплаты успешно сохранена!')
      loadSupplyRequests()
      window.dispatchEvent(new CustomEvent('supply-requests-changed'))
    } catch (err) {
      console.error(err)
      toast.error('Ошибка при сохранении даты оплаты')
    }
  }

  const handleConfirmPaySupply = async () => {
    if (!showPaySupplyModal) return
    const request = showPaySupplyModal
    const amountToPay = Number(finalPayAmount.toString().replace(/\s/g, ''))

    if (isNaN(amountToPay) || amountToPay <= 0) {
      toast.error('Введите корректную сумму оплаты')
      return
    }

    try {
      setSubmittingSupply(true)

      await api.updateSupplyRequest(request.id, { 
        status: 'paid',
        amount: amountToPay
      })

      const commenter = request.employee?.full_name || request.employee?.name || 'Сотрудник'
      await api.addExpense({
        amount: amountToPay,
        comment: `Заявка: "${request.title}" (Заявитель: ${commenter})`,
        date: new Date().toISOString().split('T')[0],
        category: 'Расходники',
        type: 'variable',
        is_paid: true
      }, request.pvz_id || activePvz.id)

      toast.success(`Заявка оплачена! Расход на ${amountToPay} ₽ автоматически учтен.`)
      setShowPaySupplyModal(null)
      setFinalPayAmount('')
      loadSupplyRequests()
      window.dispatchEvent(new CustomEvent('supply-requests-changed'))
      loadRecords()
    } catch (err) {
      console.error(err)
      toast.error('Ошибка проведения оплаты')
    } finally {
      setSubmittingSupply(false)
    }
  }

  const handleDeleteSupplyRequest = (id) => {
    setShowDeleteConfirmModal(id)
  }

  const handleConfirmDeleteSupply = async () => {
    if (!showDeleteConfirmModal) return
    try {
      await api.deleteSupplyRequest(showDeleteConfirmModal)
      toast.success('Заявка удалена')
      setShowDeleteConfirmModal(null)
      loadSupplyRequests()
      window.dispatchEvent(new CustomEvent('supply-requests-changed'))
    } catch (err) {
      console.error(err)
      toast.error('Ошибка удаления заявки')
    }
  }

  const handleSaveAdminComment = async (request) => {
    try {
      await api.updateSupplyRequest(request.id, {
        admin_comment: adminCommentText.trim() || null
      })
      
      if (adminCommentText.trim() && request.employee_id) {
        await api.notifyEmployeeAboutSupplyComment(
          request.employee_id,
          request.title,
          adminCommentText.trim()
        )
      }

      toast.success('Комментарий успешно сохранен!')
      setEditingCommentId(null)
      setAdminCommentText('')
      loadSupplyRequests()
      window.dispatchEvent(new CustomEvent('supply-requests-changed'))
    } catch (err) {
      console.error(err)
      toast.error('Ошибка при сохранении комментария')
    }
  }

  const handleCopyPayment = (text) => {
    const cleanNumber = extractCleanNumberForCopy(text)
    navigator.clipboard.writeText(cleanNumber)
    toast.success(`Номер скопирован: ${cleanNumber}`)
  }

  const statusLabels = {
    new: 'Новая',
    approved: 'Одобрена',
    paid: 'Оплачена',
    declined: 'Отклонена',
    completed: 'Выполнена'
  }

  return (
    <div style={{ marginTop: '20px' }}>
      <button
        type="button"
        onClick={async () => {
          setEditingSupplyId(null)
          setSupplyFormData({
            title: '',
            description: '',
            amount: '',
            payment_details: '',
            payment_type: 'employee_details',
            employee_id: profile?.id || '',
            file: null,
            previewUrl: '',
            photo_url: '',
            pvzId: selectedPvzIds && selectedPvzIds.length > 0 ? selectedPvzIds[0] : activePvz?.id || ''
          })
          setShowSupplyModal(true)
          
          if (profile?.id) {
            try {
              const lastPayment = await api.getLastSupplyPaymentDetails(profile.id)
              if (lastPayment) {
                setSupplyFormData(prev => ({
                  ...prev,
                  payment_details: lastPayment
                }))
              }
              await loadRecentPayments(profile.id)
            } catch (e) {
              console.warn('Error loading last payment preset:', e)
            }
          }
        }}
        style={{
          width: '100%', padding: '14px 20px', border: 'none', borderRadius: '14px', cursor: 'pointer',
          background: 'linear-gradient(135deg, var(--wb-primary), #9333ea)', color: 'white',
          fontWeight: 700, fontSize: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: '10px', boxShadow: '0 4px 16px rgba(203,17,171,0.3)', transition: 'all 0.2s',
          marginBottom: '20px'
        }}
      >
        <span style={{ fontSize: '20px' }}>＋</span> Создать заявку на хознужды
      </button>

      {loadingSupplies ? (
        <OperationsSkeleton />
      ) : supplyRequests.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon"><svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg></div>
          <div className="empty-state-title">Нет заявок</div>
          <div className="empty-state-text">Заявки на хознужды еще не создавались.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {supplyRequests.map(req => {
            const dateFormatted = new Date(req.created_at).toLocaleDateString('ru-RU', {
              day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit'
            })
            
            return (
              <div key={req.id} className="supply-request-card animate-scale-in">
                <div className="supply-request-header">
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px' }}>
                      <div className="supply-request-title" style={{ display: 'inline-block' }}>{req.title}</div>
                      {renderPvzBadge(req.pvz_id)}
                    </div>
                    {req.description && (
                      <div style={{ fontSize: '14.5px', color: 'var(--text-primary)', marginTop: '6px', fontWeight: '500', opacity: 0.95 }}>
                        {req.description}
                      </div>
                    )}
                  </div>
                  <span className={`supply-badge supply-badge-${req.status}`}>
                    {statusLabels[req.status] || req.status}
                  </span>
                </div>

                <div className="supply-request-meta">
                  <span>{req.employee?.full_name || req.employee?.name || 'Сотрудник'}</span>
                  <span>{dateFormatted}</span>
                </div>

                <div className="supply-request-details">
                  {req.amount && (
                    <div className="supply-request-amount-badge">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg> Бюджет: {formatNumberWithSpaces(req.amount)} ₽
                    </div>
                  )}

                  {/* Планируемая дата оплаты */}
                  {req.status === 'approved' && (() => {
                    const todayStr = new Date().toISOString().split('T')[0];
                    const isOverdue = req.planned_pay_date && req.planned_pay_date < todayStr;
                    return (
                      <div style={{
                        background: isOverdue ? 'rgba(239, 68, 68, 0.08)' : 'rgba(16, 185, 129, 0.05)',
                        padding: '10px 14px',
                        borderRadius: '10px',
                        borderLeft: isOverdue ? '3px solid var(--danger)' : '3px solid var(--success)',
                        fontSize: '13px',
                        marginTop: '6px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '6px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ fontWeight: 600, color: isOverdue ? 'var(--danger)' : 'var(--success)' }}>
                            {isOverdue ? 'ПРОСРОЧЕНО!' : 'Планируемая дата оплаты:'}
                          </span>
                          {req.planned_pay_date && (
                            <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
                              {new Date(req.planned_pay_date).toLocaleDateString('ru-RU')}
                            </span>
                          )}
                        </div>
                        
                        {(isAdmin || isOwner || userPermissions?.canManageOperations) && (
                          <div style={{ marginTop: '6px', width: '100%', maxWidth: '240px' }}>
                            <DatePickerField
                              value={req.planned_pay_date || ''}
                              onChange={(newVal) => handleUpdatePlannedPayDate(req.id, newVal)}
                              placeholder="Установить дату оплаты"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {req.status === 'paid' && req.planned_pay_date && (
                    <div style={{
                      background: 'var(--surface-hover)', padding: '8px 12px',
                      borderRadius: '8px', fontSize: '12px', color: 'var(--text-secondary)',
                      marginTop: '6px', display: 'flex', justifyContent: 'space-between'
                    }}>
                      <span>Планировалось оплатить:</span>
                      <span style={{ fontWeight: 600 }}>{new Date(req.planned_pay_date).toLocaleDateString('ru-RU')}</span>
                    </div>
                  )}
                  
                  {req.payment_type === 'owner_account' && (
                    <div style={{
                      background: 'rgba(203, 17, 171, 0.05)', padding: '10px 14px',
                      borderRadius: '8px', borderLeft: '3px solid var(--wb-primary)', fontSize: '13px',
                      marginTop: '6px', display: 'flex', alignItems: 'center', gap: '6px'
                    }}>
                      <div>
                        <strong style={{ color: 'var(--wb-primary)' }}>Способ заказа:</strong>
                        <span style={{ marginLeft: '4px', fontWeight: 600, color: 'var(--text-primary)' }}>
                          Заказ на аккаунт владельца (WB / Ozon)
                        </span>
                      </div>
                    </div>
                  )}

                  {req.payment_type === 'pvz_cash' && (
                    <div style={{
                      background: 'rgba(234, 179, 8, 0.05)', padding: '10px 14px',
                      borderRadius: '8px', borderLeft: '3px solid #eab308', fontSize: '13px',
                      marginTop: '6px', display: 'flex', alignItems: 'center', gap: '6px'
                    }}>
                      <div>
                        <strong style={{ color: '#eab308' }}>Способ заказа:</strong>
                        <span style={{ marginLeft: '4px', fontWeight: 600, color: 'var(--text-primary)' }}>
                          Оплата из кассы ПВЗ (или корпоративной картой)
                        </span>
                      </div>
                    </div>
                  )}

                  {(req.payment_type === 'employee_details' || !req.payment_type) && req.payment_details && (
                    <div style={{
                      background: 'var(--surface-hover)', padding: '10px 14px',
                      borderRadius: '8px', borderLeft: '3px solid var(--wb-primary)', fontSize: '13px',
                      position: 'relative', marginTop: '6px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <strong>Реквизиты для выплаты:</strong>
                        <button 
                          type="button"
                          className="btn btn-ghost btn-sm"
                          style={{ padding: '2px 6px', fontSize: '11px', color: 'var(--wb-primary)', height: 'auto', background: 'transparent', border: 'none', cursor: 'pointer' }}
                          onClick={() => handleCopyPayment(req.payment_details)}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/></svg> Копировать
                        </button>
                      </div>
                      <div style={{ color: 'var(--text-primary)', wordBreak: 'break-all', fontWeight: 600 }}>
                        {req.payment_details}
                      </div>
                    </div>
                  )}

                  {req.photo_url && (
                    <button 
                      type="button"
                      className="supply-request-photo-btn"
                      style={{ marginTop: '6px' }}
                      onClick={() => setSupplyImagePreview(req.photo_url)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg> Посмотреть фото/доказательство
                    </button>
                  )}

                  {req.admin_comment && (
                    <div style={{
                      background: 'rgba(239, 68, 68, 0.04)', padding: '10px 14px',
                      borderRadius: '10px', borderLeft: '3px solid var(--danger)', fontSize: '13px',
                      marginTop: '8px'
                    }}>
                      <strong style={{ color: 'var(--danger)', display: 'block', marginBottom: '2px' }}>Замечание руководства:</strong>
                      <div style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                        {req.admin_comment}
                      </div>
                    </div>
                  )}

                  {(isAdmin || isOwner || userPermissions?.canManageOperations) && (
                    <div style={{ marginTop: '8px' }}>
                      {editingCommentId === req.id ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', background: 'var(--surface-hover)', padding: '10px', borderRadius: '10px' }}>
                          <textarea
                            className="input"
                            rows="2"
                            placeholder="Задайте вопрос или напишите замечание к заявке..."
                            value={adminCommentText}
                            onChange={e => setAdminCommentText(e.target.value)}
                            style={{ fontSize: '13px', width: '100%', resize: 'vertical' }}
                          />
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            <button 
                              type="button"
                              className="btn btn-ghost btn-sm" 
                              style={{ padding: '4px 8px', fontSize: '11px' }}
                              onClick={() => setEditingCommentId(null)}
                            >
                              Отмена
                            </button>
                            <button 
                              type="button"
                              className="btn btn-primary btn-sm" 
                              style={{ padding: '4px 8px', fontSize: '11px', background: 'var(--wb-primary)' }}
                              onClick={() => handleSaveAdminComment(req)}
                            >
                              Сохранить
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          style={{ padding: '2px 6px', fontSize: '11px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '4px' }}
                          onClick={() => {
                            setEditingCommentId(req.id)
                            setAdminCommentText(req.admin_comment || '')
                          }}
                        >
                          {req.admin_comment ? 'Изменить замечание' : 'Добавить замечание / вопрос'}
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginTop: '10px', paddingTop: '10px', borderTop: '1px dashed var(--surface-border)' }}>
                  
                  {(isAdmin || isOwner || userPermissions?.canManageOperations) && (
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {req.status === 'new' && (
                        <>
                          <button 
                            type="button"
                            className="btn btn-secondary btn-sm"
                            style={{ padding: '6px 12px', fontSize: '12px' }}
                            onClick={() => handleUpdateSupplyStatus(req, 'approved')}
                          >
                            Одобрить
                          </button>
                          <button 
                            type="button"
                            className="btn btn-ghost btn-sm text-danger"
                            style={{ padding: '6px 12px', fontSize: '12px' }}
                            onClick={() => handleUpdateSupplyStatus(req, 'declined')}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg> Отклонить
                          </button>
                        </>
                      )}

                      {req.status === 'approved' && (
                        <>
                          <button 
                            type="button"
                            className="btn btn-primary btn-sm"
                            style={{ padding: '6px 12px', fontSize: '12px' }}
                            onClick={() => handleUpdateSupplyStatus(req, 'paid')}
                          >
                            Оплатить (Внести расход)
                          </button>
                          <button 
                            type="button"
                            className="btn btn-ghost btn-sm text-danger"
                            style={{ padding: '6px 12px', fontSize: '12px' }}
                            onClick={() => handleUpdateSupplyStatus(req, 'declined')}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg> Отклонить
                          </button>
                        </>
                      )}

                      {req.status === 'paid' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--success)', fontSize: '13px', fontWeight: 600 }}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg> Оплачено (Расход занесен)
                        </div>
                      )}
                    </div>
                  )}

                  {((req.employee_id === profile.id && req.status === 'new') || isAdmin || isOwner || userPermissions?.canManageOperations) && (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      style={{ padding: '6px 12px', fontSize: '12px', color: 'var(--text-secondary)' }}
                      onClick={async () => {
                        setEditingSupplyId(req.id)
                        setSupplyFormData({
                          title: req.title,
                          description: req.description || '',
                          amount: req.amount ? formatNumberWithSpaces(req.amount) : '',
                          payment_details: req.payment_details || '',
                          payment_type: req.payment_type || 'employee_details',
                          employee_id: req.employee_id || '',
                          file: null,
                          previewUrl: req.photo_url || '',
                          photo_url: req.photo_url || '',
                          pvzId: req.pvz_id || ''
                        })
                        setShowSupplyModal(true)
                        if (req.employee_id) {
                          await loadRecentPayments(req.employee_id)
                        }
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg> Редактировать
                    </button>
                  )}

                  {((req.employee_id === profile.id && req.status === 'new') || isAdmin || isOwner || userPermissions?.canManageOperations) && (
                    <button 
                      type="button"
                      className="btn btn-ghost btn-sm text-danger" 
                      style={{ marginLeft: 'auto', padding: '6px 12px' }}
                      onClick={() => handleDeleteSupplyRequest(req.id)}
                      title="Удалить заявку"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight:'4px',verticalAlign:'middle'}}><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg> Удалить
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Модалка создания/редактирования заявки на хознужды */}
      <Modal 
        isOpen={showSupplyModal} 
        onClose={() => {
          setShowSupplyModal(false)
          setEditingSupplyId(null)
          setSupplyFormData({
            title: '',
            description: '',
            amount: '',
            payment_details: '',
            payment_type: 'employee_details',
            employee_id: '',
            file: null,
            previewUrl: '',
            photo_url: '',
            pvzId: selectedPvzIds && selectedPvzIds.length > 0 ? selectedPvzIds[0] : activePvz?.id || ''
          })
        }} 
        title={editingSupplyId ? "Редактировать заявку" : "Создать заявку на хознужды"}
      >
        <form onSubmit={handleSubmitSupplyRequest} style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '8px 0 16px' }}>
          {(isAdmin || isOwner || userPermissions?.canManageOperations) && (
            <div className="input-group">
              <CustomSelect
                label="Сотрудник (получатель выплаты) *"
                value={supplyFormData.employee_id || profile?.id || ''}
                onChange={async (val) => {
                  setSupplyFormData(prev => ({
                    ...prev,
                    employee_id: val
                  }));
                  if (val) {
                    try {
                      const lastPayment = await api.getLastSupplyPaymentDetails(val)
                      setSupplyFormData(prev => ({
                        ...prev,
                        payment_details: lastPayment || ''
                      }));
                      await loadRecentPayments(val)
                    } catch (err) {
                      console.warn('Error loading employee payment preset:', err)
                    }
                  }
                }}
                options={[
                  { value: profile?.id, label: profile?.full_name || profile?.name || 'Вы (Владелец)' },
                  ...employees.filter(emp => !emp.is_legacy && emp.id !== profile?.id).map(emp => ({
                    value: emp.id,
                    label: emp.full_name || emp.name,
                  })),
                ]}
              />
            </div>
          )}

          {selectedPvzIds && selectedPvzIds.length > 1 && (
            <div className="input-group">
              <CustomSelect
                label="Пункт выдачи *"
                value={supplyFormData.pvzId || ''}
                onChange={val => setSupplyFormData({ ...supplyFormData, pvzId: val })}
                placeholder="Выберите ПВЗ"
                options={(userPvzs || []).filter(p => selectedPvzIds.includes(p.id)).map(pvz => ({
                  value: pvz.id,
                  label: pvz.name,
                }))}
              />
            </div>
          )}

          <div className="input-group">
            <label>Что требуется купить / заказать *</label>
            <input 
              className="input" 
              type="text" 
              placeholder="Скотч 5 шт, файлы 1 уп, моющее средство"
              value={supplyFormData.title}
              onChange={e => setSupplyFormData({ ...supplyFormData, title: e.target.value })}
              required 
            />
          </div>

          <div className="input-group">
            <label>Описание / Детали (необязательно)</label>
            <textarea 
              className="input" 
              rows="3"
              placeholder="Укажите конкретные требования к товару, если они есть (например: скотч широкий прозрачный)"
              value={supplyFormData.description}
              onChange={e => setSupplyFormData({ ...supplyFormData, description: e.target.value })}
            />
          </div>

          <div className="input-group">
            <label>Примерный бюджет / Стоимость (₽, необязательно)</label>
            <input 
              className="input" 
              type="text" 
              placeholder="1 000"
              value={supplyFormData.amount}
              onChange={e => setSupplyFormData({ ...supplyFormData, amount: formatNumberWithSpaces(e.target.value) })}
            />
          </div>

          <div className="input-group">
            <CustomSelect
              label="Способ заказа / оплаты *"
              value={supplyFormData.payment_type || 'employee_details'}
              onChange={val => {
                setSupplyFormData(prev => ({
                  ...prev,
                  payment_type: val,
                  payment_details: val === 'employee_details' ? prev.payment_details : ''
                }));
              }}
              options={[
                { value: 'employee_details', label: 'Выплата сотруднику по реквизитам' },
                { value: 'owner_account', label: 'Заказ на аккаунт владельца (WB / Ozon)' },
                { value: 'pvz_cash', label: 'Оплата из кассы ПВЗ / Корпоративной картой' },
              ]}
            />
          </div>

          {(supplyFormData.payment_type === 'employee_details' || !supplyFormData.payment_type) ? (
            <div className="input-group">
              <label>Реквизиты для выплаты *</label>
              <textarea 
                className="input" 
                rows="2"
                placeholder="Номер карты, телефон с СБП (+банк)"
                value={supplyFormData.payment_details}
                onChange={e => setSupplyFormData({ ...supplyFormData, payment_details: formatPaymentDetails(e.target.value) })}
                required
              />

              {recentPayments && recentPayments.length > 0 && (
                <div style={{ marginTop: '8px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '6px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                    Быстрый выбор из недавних реквизитов:
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {recentPayments.map((payment, idx) => {
                      let shortLabel = payment;
                      if (payment.length > 25) {
                        shortLabel = payment.substring(0, 22) + '...';
                      }
                      
                      return (
                        <button
                          key={idx}
                          type="button"
                          className="btn btn-sm btn-ghost"
                          style={{
                            padding: '4px 10px',
                            height: 'auto',
                            fontSize: '11px',
                            border: '1px solid var(--surface-border)',
                            borderRadius: '6px'
                          }}
                          onClick={() => setSupplyFormData(prev => ({ ...prev, payment_details: payment }))}
                        >
                          {shortLabel}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ) : null}

          <div className="input-group">
            <label>Прикрепить фото / доказательство (необязательно)</label>
            <div className="file-upload-wrapper" style={{ marginTop: '4px' }}>
              <label htmlFor="supply-file-input" className="file-upload-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', border: '2px dashed var(--surface-border)', borderRadius: '10px', cursor: 'pointer', background: 'var(--surface-hover)' }}>
                <div style={{ textAlign: 'center' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--text-tertiary)', marginBottom: '6px' }}><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                  <br />
                  {supplyFormData.file ? (
                    <span style={{ color: 'var(--wb-primary)', fontWeight: 600 }}>
                      {supplyFormData.file.name}
                    </span>
                  ) : (
                    <span>Нажмите для выбора фото или перетащите файл</span>
                  )}
                </div>
              </label>
              <input 
                id="supply-file-input"
                className="file-input-hidden" 
                type="file" 
                accept="image/*"
                style={{ display: 'none' }}
                onChange={e => {
                  const file = e.target.files[0];
                  if (file) {
                    setSupplyFormData({ 
                      ...supplyFormData, 
                      file, 
                      previewUrl: URL.createObjectURL(file) 
                    });
                  }
                }}
              />
              {(supplyFormData.file || supplyFormData.photo_url) && (
                <button 
                  type="button" 
                  className="file-upload-remove-btn"
                  style={{ marginTop: '8px', color: 'var(--danger)', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '12px' }}
                  onClick={() => setSupplyFormData({ ...supplyFormData, file: null, previewUrl: '', photo_url: '' })}
                >
                  Удалить фото ×
                </button>
              )}
            </div>
            {supplyFormData.previewUrl && (
              <img 
                src={proxyStorageUrl(supplyFormData.previewUrl)} 
                alt="Превью" 
                style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', marginTop: '10px' }} 
              />
            )}
          </div>

          <div style={{ display: 'flex', gap: '12px', marginTop: '10px' }}>
            <button 
              type="button" 
              className="btn btn-ghost" 
              style={{ flex: 1 }} 
              onClick={() => {
                setShowSupplyModal(false)
                setEditingSupplyId(null)
                setSupplyFormData({
                  title: '',
                  description: '',
                  amount: '',
                  payment_details: '',
                  payment_type: 'employee_details',
                  employee_id: '',
                  file: null,
                  previewUrl: '',
                  photo_url: ''
                })
              }}
            >
              Отмена
            </button>
            <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={submittingSupply}>
              {submittingSupply ? <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{animation:'spin 1s linear infinite'}}><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg> Сохранение...</> : (editingSupplyId ? 'Сохранить изменения' : 'Отправить заявку')}
            </button>
          </div>
        </form>
      </Modal>

      {/* Модалка оплаты заявки */}
      <Modal 
        isOpen={!!showPaySupplyModal} 
        onClose={() => setShowPaySupplyModal(null)} 
        title="Проведение оплаты заявки"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '8px 0 16px' }}>
          <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-secondary)' }}>
            Укажите фактическую сумму оплаты по заявке <strong>«{showPaySupplyModal?.title}»</strong>.
            Сумма будет автоматически проведена как переменный расход («Расходники») в вашем учете!
          </p>

          <div className="input-group">
            <label>Сумма к оплате (₽)</label>
            <input 
              className="input" 
              type="text" 
              value={finalPayAmount}
              onChange={e => setFinalPayAmount(formatNumberWithSpaces(e.target.value))}
              placeholder="0"
              required
            />
          </div>

          <div style={{ display: 'flex', gap: '12px', marginTop: '10px' }}>
            <button type="button" className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setShowPaySupplyModal(null)}>
              Отмена
            </button>
            <button type="button" className="btn btn-primary" style={{ flex: 1 }} onClick={handleConfirmPaySupply} disabled={submittingSupply}>
              {submittingSupply ? <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{animation:'spin 1s linear infinite'}}><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg> Проведение...</> : 'Подтвердить и оплатить'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Модалка подтверждения удаления заявки */}
      <Modal 
        isOpen={!!showDeleteConfirmModal} 
        onClose={() => setShowDeleteConfirmModal(null)} 
        title="Подтверждение удаления"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '8px 0 16px', alignItems: 'center', textAlign: 'center' }}>
          <div style={{ marginBottom: '8px', display:'flex', justifyContent:'center' }}><svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--danger)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg></div>
          <p style={{ margin: 0, fontSize: '15px', color: 'var(--text-primary)', fontWeight: 600 }}>
            Вы действительно хотите удалить эту заявку на хознужды?
          </p>
          <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)' }}>
            Это действие безвозвратно удалит все данные заявки. Восстановить их будет невозможно.
          </p>

          <div style={{ display: 'flex', gap: '12px', width: '100%', marginTop: '10px' }}>
            <button type="button" className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setShowDeleteConfirmModal(null)}>
              Отмена
            </button>
            <button 
              type="button" 
              className="btn btn-danger" 
              style={{ flex: 1, background: 'var(--danger)', color: 'white', fontWeight: 600 }} 
              onClick={handleConfirmDeleteSupply}
            >
              Удалить
            </button>
          </div>
        </div>
      </Modal>

      {/* Лайтбокс для просмотра фото */}
      {supplyImagePreview && (
        <div className="supply-lightbox-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setSupplyImagePreview(null)}>
          <div className="supply-lightbox-content" style={{ position: 'relative', maxWidth: '90%', maxHeight: '90%' }} onClick={e => e.stopPropagation()}>
            <button className="supply-lightbox-close" style={{ position: 'absolute', top: '-40px', right: '0', background: 'transparent', border: 'none', color: 'white', fontSize: '30px', cursor: 'pointer' }} onClick={() => setSupplyImagePreview(null)}>×</button>
            <img src={proxyStorageUrl(supplyImagePreview)} alt="Доказательство" className="supply-lightbox-img" style={{ maxWidth: '100%', maxHeight: '90vh', objectFit: 'contain', borderRadius: '8px' }} />
          </div>
        </div>
      )}
    </div>
  )
}
